import { Page, SortOption } from "@/types/search-page";
import { CertificationVM } from "@/types/certifications";
import { Patch, PatchVM } from "@/types/json-patch";
import { FormOption } from "@/types/form";

export enum DisbursementStatuses {
    PRE_DISBURSEMENT_CANCELLATION = 2,
    ON_HOLD = 3,
    CERTIFIED_ON_HOLD = 4,
    SCHEDULED_DISBURSEMENT = 5,
    DISBURSEMENT_PENDING = 6,
    DISBURSED = 7,
    POST_DISBURSEMENT_CANCELLATION = 8,
    POST_DISBURSEMENT_PARTIAL_CANCELLATION = 9,
}

export class DisbursementStatusIds {
    public static get PRE_DISBURSEMENT_CANCELLATION(): number {
        return DisbursementStatuses.PRE_DISBURSEMENT_CANCELLATION;
    }
    public static get ON_HOLD(): number {
        return DisbursementStatuses.ON_HOLD;
    }
    public static get CERTIFIED_ON_HOLD(): number {
        return DisbursementStatuses.CERTIFIED_ON_HOLD;
    }
    public static get SCHEDULED_DISBURSEMENT(): number {
        return DisbursementStatuses.SCHEDULED_DISBURSEMENT;
    }
    public static get DISBURSEMENT_PENDING(): number {
        return DisbursementStatuses.DISBURSEMENT_PENDING;
    }
    public static get DISBURSED(): number {
        return DisbursementStatuses.DISBURSED;
    }
    public static get POST_DISBURSEMENT_CANCELLATION(): number {
        return DisbursementStatuses.POST_DISBURSEMENT_CANCELLATION;
    }
    public static get POST_DISBURSEMENT_PARTIAL_CANCELLATION(): number {
        return DisbursementStatuses.POST_DISBURSEMENT_PARTIAL_CANCELLATION;
    }
}

export interface PagedDisbursementSearchResponse {
    page: Page;
    totalCount: number;
    items: DisbursementSearchResultModel[];
}

export interface DisbursementSearchResultModel {
    id: number;
    certificationId: number;
    referenceId?: string;
    cluid?: string | null;
    lenderId: number;
    lenderName: string;
    lenderDisplayName: string;
    borrowerName: string;
    borrowerFullName: string;
    borrowerInitials: string;
    borrowerLastName: string;
    disbursementStatusTypeName: string;
    disbursementStatusTypeDescription: string;
    disbursementMethodTypeName?: string | null;
    disbursementMethodTypeDescription: string;
    disbursementStatusType: number;
    disbursementMethodType: number;
    disbursementAmount: number;
    disbursementAmountFormatted: string;
    createdAtUtc: Date | string;
    disbursementDate: Date | string;
    disbursementDateDisplay: string;
}

export interface SearchAndFilterDisbursementsRequest {
    search?: string | null;
    productTypeId: number | null;
    disbursementMethodTypes: number[];
    disbursementStatusTypes: number[];
    lenderIds: number[];
    page?: Page | null;
    sortOptions: SortOption[];
}

export interface DisbursementModel {
    id: number;
    certificationId: number;
    disbursementStatusTypeId: number;
    disbursementMethodTypeId: number;
    certificationDisbursementStatusReasonTypeId: number;
    sequenceNumber: string;
    disbursementAmount: number;
    disbursementDate: Date | string;
    batchDate?: Date | string;
}

export interface SchoolDisbursement {
    id: number;
    schoolId: number;
    disbursementTypeId: number;
    preferred: boolean;
    isUsed: boolean;
}

export interface DisbursementFormOptions {
    disbursementMethodTypes: FormOption[];
    disbursementStatusTypes: FormOption[];
    certificationDisbursementStatusReasonTypes: FormOption[];
    postDisbursementChangeReasonTypes: FormOption[];
    schoolDisbursements: SchoolDisbursement[];
    preferredSchoolDisbursementTypeId: number;
    preferredSchoolDisbursementTypeName: string;
}

export interface DisbursementVM {
    details: DisbursementFormVM;
    form: DisbursementFormVM;
    formOptions: DisbursementFormOptions;
}

export interface DisbursementDetailsVM {
    id: number;
    certificationId: number;
    sequenceNumber: string;
    disbursementStatusTypeId: number;
    disbursementMethodTypeId: number;
    certificationDisbursementStatusReasonTypeId?: number;
    disbursementAmount: number;
    disbursementDate: string;
    disbursementStatusTypeName: string;
    disbursementMethodTypeName: string;
    certificationDisbursementStatusReasonTypeName?: string | null;
    disbursementAmountDisplay: string;
    disbursementDateDisplay: string;
    isEditing: boolean;
    isDisbursed: boolean;
    allowPostDisbursementChange: boolean;
    maxAmountForPostDisbursementChange: number;
    hasPendingEdits: boolean;
    hasPendingPostDisbursementChange: boolean;
    pendingPostDisbursementChange?: CreatePostDisbursementChangeRequest | null;
    latestPostDisbursementChange : PostDisbursementChangeModel | null;
    // Client-side only: set on placeholders created by handleAddNew. Backend IDs can be
    // negative (LOC-migrated records), so we can't infer "unsaved" from the id sign.
    isNew?: boolean;
}

export interface DisbursementFormVM {
    id: number;
    certificationId: number;
    sequenceNumber?: string | null;
    disbursementStatusTypeId: number;
    disbursementMethodTypeId: number;
    certificationDisbursementStatusReasonTypeId?: number | null;
    disbursementAmount: number | null;
    disbursementDate: string | null;
    disbursementStatusTypeName?: string | null;
    disbursementMethodTypeName?: string | null;
    certificationDisbursementStatusReasonTypeName?: string | null;
    disbursementAmountDisplay?: string | null;
    disbursementDateDisplay?: string | null;
    isEditing: boolean;
    isDisbursed: boolean;
    allowPostDisbursementChange: boolean;
    maxAmountForPostDisbursementChange: number;
    hasPendingEdits?: boolean;
    hasPendingPostDisbursementChange?: boolean;
    pendingPostDisbursementChange?: CreatePostDisbursementChangeRequest | null;
    pendingPostDisbursementChangePatch?: PostDisbursementChangePatchRequest | null;
    latestPostDisbursementChange : PostDisbursementChangeModel | null;
    isNew?: boolean;
}

export interface AddDisbursementRequest {
    disbursement: DisbursementFormVM;
    certifiedAmount: number;
    certificationSequenceNumber: string;
    certificationId: number;
    loanId: number;
    appId: number;
    cluid: string;
    lenderId: number;
    productTypeId: number;
    referenceId: string;
    borrowerFirstName: string;
    borrowerLastName: string;
    disbursementDateValidated: boolean;
    formOptions: DisbursementFormOptions;
}

export interface DisbursementPatchRequest {
    patchDocument: Patch[];
    patchesVM: PatchVM[];
    applicationId: number;
    loanId: number;
    certificationId: number;
    disbursementId: number;
    certificationSequenceNumber: string;
    disbursementSequenceNumber?: string | null;
    postDisbursementChange?: CreatePostDisbursementChangeRequest;
    postDisbursementChangePatch?: PostDisbursementChangePatchRequest;
}

export interface ValidateDisbursementDateRequest {
    productTypeId: number;
    certificationDate: Date | string | null;
    disbursementMethodTypeId: number;
    providedDate: Date | string;
}

export interface ValidateDisbursementDateResponse {
    success: boolean;
    errors: string[];
    isValid: boolean;
    disbursementDate: Date;
    disbursementDateInput: string;
    disbursementDateDisplay: string;
    validatedDisbursementDate: Date;
    validatedDisbursementDateInput: string;
    validatedDisbursementDateDisplay: string;
}

export interface DisbursementBatchSaveRequest {
    certificationId: number;
    applicationId: number;
    loanId: number;
    updateDisbursements: DisbursementPatchRequest[];
    addDisbursements?: AddDisbursementRequest[];
    generateModificationFile?: boolean;
}

export interface CreatePostDisbursementChangeRequest {
    disbursementAmount: number;
    disbursementAmountDisplay: string;
    postDisbursementChangeReasonTypeId: number | null;
    settleDate: string | Date;
    effectiveDate: string | Date;
    processingDate: string | Date;
    disbursementMethodTypeId: number;
}

export interface ValidateDisbursementsResponse {
    success: boolean;    
    errors: string[];
    results: boolean;    
}

export interface DisbursementBatchSaveResponse {
    success: boolean;    
    errors: string[];
    results: DisbursementVM[];    
}

export interface ToggleEvent {
    index: number;
    flag: boolean;
}

export enum DisbursementBatchType {
    LOAN = 1,
    REFI = 2,
    PEL = 3
}

export interface DisbursementAddedEvent {
    disbursement: DisbursementVM;
    disbursementIndex: number;
}

export enum PostDisbursementChangeReasonTypes {
    NOT_PROVIDED = 12
}

export interface DisbursementChangesSummary {
  certSeq: string;
  disbSeq: string;
  disbursementId: number;
  isNew?: boolean;    
  fieldChanges?: {
    field: string;
    previous: string;
    new: string;
  }[];  
  postDisbChange?: {
    isEdit?: boolean;
    newStatus?: string;
    newAmount?: string;
    processingDate?: string;
    effectiveDate?: string;
    settleDate?: string;
    method?: string;
    reason?: string;    
    changes?: {
      field: string;
      previous: string;
      new: string;
    }[];
  };  
  newDisbursement?: {
    status: string;
    amount: string;
    date: string;
    method: string;
  };
}

export interface PostDisbursementChangeModel {
    disbursementAmountLedgerId: number;
    postDisbursementChangeReasonTypeId: number;
    settleDate: string | Date;
    effectiveDate: string | Date;
    processingDate: string | Date;
    disbursementMethodTypeId: number;
}

export interface PostDisbursementChangePatchRequest {
  postDisbursementChangeAmountLedgerId: number;
  disbursementId: number;
  certificationId: number;
  patchDocument: Patch[];
  patchesVM: PatchVM[];
}